import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "./PageTitleCompoent";
import "../css/DoctorProfileScreen.css";
import PhoneIcon from "../assets/Icons/PhoneIcons";
import MailIcons from "../assets/Icons/MailIcons";
import LocationIcon from "../assets/Icons/LocationIcon";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import api from "../api";
import PawAnimation from "../components/PawAnimation";
import DocumentViewerModal from "../components/DocumentViewerModal";

export default function DoctorProfileScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeDoctor = location.state?.doctor;

  const doctorId = routeDoctor?.id;
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [doctorToDelete, setDoctorToDelete] = useState(null);
  const [isActive, setIsActive] = useState(false);

  // NEW: Document modal state
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [fees, setFees] = useState({});

  useEffect(() => {
    if (!doctorId) {
      toast.error("Doctor ID missing");
      navigate(-1);
      return;
    }

    const fetchDoctorDetails = async () => {
      try {
        const response = await api.get(`/doctor-profiles/${doctorId}`);
        const data = response.data;

        const summary = data.appointment_fee_collection_summary || {};

        // ✅ Normalize document fields into an array for the modal
        const documentsArray = [
          data.id_proof,
          data.address_proof,
          data.business_registration,
          data.service_certifications,
          data.passport_photo,
          data.bank_details,
        ].filter((doc) => doc && doc.url); // only include valid ones

        setDoctor({
          id: data.id,
          name: data.name,
          specialization: data.specialization,
          gstn: data.gstn,
          clinic_name: data.clinic_name,
          phone_number: data.phone_number,
          email: data.email,
          address: data.address,
          profile_image: data.profile_image,
          status: data.status,
          rating: data.rating,
          reviews_count: data.reviews_count,
          bio: data.bio,
          uploaded_documents: documentsArray, // ✅ use normalized array
          overall_appointment: summary.overall_appointment ?? 0,
          overall_doctor_fees: summary.overall_doctor_fees ?? 0,
          pawfriends_commission_percentage:
            summary.pawfriends_commission_percentage ?? 0,
          pawfriends_profit: summary.pawfriends_profit ?? 0,
        });

        const savedFees = localStorage.getItem(`doctorFees_${doctorId}`);
        if (savedFees) {
          setFees(JSON.parse(savedFees));
        } else {
          setFees({
            per_visit_fee: data.per_visit_fee,
            commission_percentage: data.commission_percentage,
            overall_fee: data.overall_fee,
            overall_appointment: data.overall_appointment,
            overall_doctor_fees: data.overall_doctor_fees,
            pawfriends_commission_percentage:
              data.pawfriends_commission_percentage,
            pawfriends_profit: data.pawfriends_profit,
          });
        }

        // ✅ Check for saved status in localStorage
        const savedStatus = localStorage.getItem(`doctorStatus_${doctorId}`);
        const currentStatus = savedStatus || data.status;
        setIsActive(currentStatus === "active");
      } catch (error) {
        console.error("Error fetching doctor details:", error);
        toast.error("Failed to fetch doctor details");
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorDetails();
  }, [doctorId, navigate]);

  const handleStatusToggle = async () => {
    const newStatus = !isActive;
    const updatedStatus = newStatus ? "active" : "inactive";

    setIsActive(newStatus);

    try {
      await api.patch(`/doctor-profiles/`, {
        doctor_profile_id: doctorId,
        account_status: updatedStatus,
      });

      setDoctor((prev) => ({
        ...prev,
        status: updatedStatus,
      }));

      // ✅ Save status to localStorage to sync with AllDoctorScreen
      localStorage.setItem(`doctorStatus_${doctorId}`, updatedStatus);

      // ✅ Trigger storage event for same-tab update
      window.dispatchEvent(
        new StorageEvent("storage", {
          key: `doctorStatus_${doctorId}`,
          newValue: updatedStatus,
        })
      );

      toast.success("Status updated successfully", { id: "status-toast" });
    } catch (error) {
      console.error("Error updating doctor status:", error);
      setIsActive(!newStatus);
      toast.error("Failed to update status", { id: "status-toast" });
    }
  };

  const handleClose = () => navigate(-1);

  const openDeleteModal = () => {
    setDoctorToDelete(doctor);
    setIsModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsModalOpen(false);
    setDoctorToDelete(null);
  };

  const handleConfirmDelete = () => {
    setIsModalOpen(false);
    // ✅ Clean up localStorage
    localStorage.removeItem(`doctorStatus_${doctorId}`);
    localStorage.removeItem(`doctorFees_${doctorId}`);
    toast.success(`${doctorToDelete?.name || "Doctor"} deleted successfully!`);
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFees((prev) => ({ ...prev, [name]: value }));
  };

  const saveFees = async () => {
    try {
      const formData = new FormData();
      formData.append(
        "pawfriends_commission_percentage",
        fees.pawfriends_commission_percentage
      );

      const response = await api.patch(
        `/doctor-profiles/${doctorId}/`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setDoctor((prev) => ({
        ...prev,
        pawfriends_commission_percentage: fees.pawfriends_commission_percentage,
      }));

      localStorage.setItem(`doctorFees_${doctorId}`, JSON.stringify(fees));
      toast.success("Commission updated successfully!");
    } catch (error) {
      console.error("Error updating commission:", error);
      toast.error("Failed to update commission");
    }
  };

  const handleBlur = async () => {
    setEditingField(null);
    await saveFees();
  };

  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setEditingField(null);
      await saveFees();
    }
  };

  const renderEditableValue = (label, prefix, name, suffix = "") => {
    const getDisplayValue = (val) => {
      if (val === null || val === undefined || val === "") return "";
      const num = Number(val);
      return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    };

    const getInputValue = (val) => {
      if (val === null || val === undefined || val === "") return "";
      return Number(val);
    };

    const currentValue =
      fees[name] !== undefined && fees[name] !== null
        ? fees[name]
        : doctor[name] || "";

    return (
      <p>
        {label} {prefix}
        {editingField === name ? (
          <input
            type="number"
            name={name}
            value={getInputValue(currentValue)}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            className="editable-input"
          />
        ) : (
          <span
            className="editable-text"
            onClick={() => setEditingField(name)}
            style={{ cursor: "pointer" }}
          >
            {getDisplayValue(currentValue)}
          </span>
        )}
        {suffix}
      </p>
    );
  };

  // NEW: Functions for document modal
  const openDocModal = () => setIsDocModalOpen(true);
  const closeDocModal = () => setIsDocModalOpen(false);

  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  if (!doctor) {
    return <div>Doctor data not found</div>;
  }

  return (
    <div className="doctor-profile-details-container">
      <Sidebar />
      <div className="doctor-profile-details-main">
        <Header />

        <motion.div
          className="doctor-profile-details-content"
          style={{ textAlign: "left" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <PageTitle
            title="Doctor Profile"
            subtitle={`Detailed information about Dr. ${doctor.name}`}
            showBackArrow={false}
          />

          <motion.div
            className="doctor-profile-details-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="icon-container" onClick={handleClose} title="Close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M20.48 3.512a11.97 11.97 0 0 0-8.486-3.514C5.366-.002-.007 5.371-.007 11.999c0 3.314 1.344 6.315 3.516 8.487A11.97 11.97 0 0 0 11.995 24c6.628 0 12.001-5.373 12.001-12.001c0-3.314-1.344-6.315-3.516-8.487m-1.542 15.427a9.8 9.8 0 0 1-6.943 2.876c-5.423 0-9.819-4.396-9.819-9.819a9.8 9.8 0 0 1 2.876-6.943a9.8 9.8 0 0 1 6.942-2.876c5.422 0 9.818 4.396 9.818 9.818a9.8 9.8 0 0 1-2.876 6.942z"
                />
                <path
                  fill="currentColor"
                  d="m13.537 12l3.855-3.855a1.091 1.091 0 0 0-1.542-1.541l.001-.001l-3.855 3.855l-3.855-3.855A1.091 1.091 0 0 0 6.6 8.145l-.001-.001l3.855 3.855l-3.855 3.855a1.091 1.091 0 1 0 1.541 1.542l.001-.001l3.855-3.855l3.855 3.855a1.091 1.091 0 1 0 1.542-1.541l-.001-.001z"
                />
              </svg>
            </div>

            {/* Profile Header Section */}
            <div className="doctor-profile-details-header">
              <img
                src={doctor.profile_image}
                alt={`Dr. ${doctor.name}`}
                className="doctor-profile-details-avatar"
              />

              <div className="doctor-profile-details-info">
                <div className="doctor-profile-details-name-row">
                  <h2 className="doctor-profile-details-name">{doctor.name}</h2>

                  <div className="doctor-profile-details-status-toggle">
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={handleStatusToggle}
                      />
                      <span className="slider round" />
                    </label>
                    <span
                      className={`status-label ${
                        isActive ? "active" : "inactive"
                      }`}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <p className="doctor-profile-details-subtitle">
                  {doctor.specialization
                    ?.split(",") // split by comma
                    .map((s) =>
                      s
                        .trim()
                        .replace(/_/g, " ") // replace underscores
                        .split(" ") // split words
                        .map(
                          (w) =>
                            w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
                        ) // capitalize each word
                        .join(" ")
                    )
                    .join(", ")}
                </p>

                <div className="doctor-profile-details-contact">
                  <div className="doctor-profile-details-contact-list">
                    <div className="doctor-profile-details-contact-subitem">
                      <span className="doctor-profile-details-contact-label">
                        GSTN:
                      </span>
                      <span>{doctor.gstn}</span>
                    </div>

                    <div className="doctor-profile-details-contact-item">
                      <span>{doctor.clinic_name}</span>
                    </div>

                    <div className="doctor-profile-details-contact-item">
                      <PhoneIcon />
                      <span>{doctor.phone_number}</span>
                    </div>

                    <div className="doctor-profile-details-contact-item">
                      <MailIcons />
                      <span>{doctor.email}</span>
                    </div>

                    <div className="doctor-profile-details-contact-item">
                      <LocationIcon />
                      <span>{doctor.address}</span>
                    </div>

                    {/* Rating + Delete */}
                    <div className="doctor-profile-details-rating-row">
                      <div className="doctor-profile-details-rating-info">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={30.83}
                          height={30.26}
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="#FFC107"
                            d="m12 17.275l-4.15 2.5q-.275.175-.575.15t-.525-.2t-.35-.437t-.05-.588l1.1-4.725L3.775 10.8q-.25-.225-.312-.513t.037-.562t.3-.45t.55-.225l4.85-.425l1.875-4.45q.125-.3.388-.45t.537-.15t.537.15t.388.45l1.875 4.45l4.85.425q.35.05.55.225t.3.45t.038.563t-.313.512l-3.675 3.175l1.1 4.725q.075.325-.05.588t-.35.437t-.525.2t-.575-.15z"
                          />
                        </svg>{" "}
                        <span>{doctor.rating}</span>
                        <span className="doctor-profile-details-review-count">
                          ({doctor.reviews_count} reviews)
                        </span>
                      </div>

                      <motion.button
                        className="doctor-profile-details-delete-btn"
                        title="Delete"
                        aria-label="Delete Doctor Profile"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={openDeleteModal}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="40.91"
                          height="46.02"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="#FF3D00"
                            d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zm2-4h2V8H9zm4 0h2V8h-2z"
                          />
                        </svg>
                      </motion.button>
                    </div>

                    {/* UPDATED: Uploaded Docs with Modal */}
                    <div className="doctor-profile-details-contact-item doctor-profile-details-documents-row">
                      <p>Uploaded Documents</p>
                      <button
                        className="doctor-profile-details-view-btn"
                        onClick={openDocModal}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={24}
                          height={24}
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="#eba8bf"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          >
                            <path d="M12 5c-6.307 0-9.367 5.683-9.91 6.808a.44.44 0 0 0 0 .384C2.632 13.317 5.692 19 12 19s9.367-5.683 9.91-6.808a.44.44 0 0 0 0-.384C21.368 10.683 18.308 5 12 5"></path>
                            <circle cx={12} cy={12} r={3}></circle>
                          </g>
                        </svg>{" "}
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <motion.div
              className="doctor-profile-details-description"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <strong className="doctor-profile-details-description-heading">
                Dr. {doctor.name} –{" "}
                {doctor.specialization
                  ?.split(",")
                  .map((s) =>
                    s
                      .trim()
                      .replace(/_/g, " ") // replace underscores
                      .split(" ") // split words
                      .map(
                        (w) =>
                          w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
                      ) // capitalize each word
                      .join(" ")
                  )
                  .join(", ")}
              </strong>
              <p className="doctor-profile-details-description-paragraph">
                {doctor.bio}
              </p>
            </motion.div>
            {/* Fees Section */}
            <div className="doctor-profile-fees-container">
              <motion.div
                className="doctor-profile-fee-card"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
              >
                <h4 className="fee-card-heading">For This Week</h4>

                <div className="fee-card-content">
                  <div className="fee-card-item">
                    <p>
                      Appointments:{" "}
                      <span>{doctor.overall_appointment ?? "0"}</span>
                    </p>
                  </div>

                  <div className="fee-card-item">
                    <p>
                      Doctor Fees: ₹
                      <span>{doctor.overall_doctor_fees ?? "0"}</span>
                    </p>
                  </div>

                  <div className="fee-card-item">
                    {/* ✅ Only this one remains editable */}
                    {renderEditableValue(
                      "PawFriends Commission:",
                      "%",
                      "pawfriends_commission_percentage"
                    )}
                  </div>

                  <div className="fee-card-item">
                    <p>
                      PawFriends Profit: ₹
                      <span>{doctor.pawfriends_profit ?? "0"}</span>
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Appointment Section */}
            <div className="doctor-profile-details-headertwo">
              <strong className="doctor-profile-details-description-headingtwo">
                View All Doctor Appointment Details
              </strong>

              <button
                className="doctor-profile-details-appointment-btn"
                onClick={() =>
                  navigate("/appointment_detail", {
                    state: { doctorId: doctor.id },
                  })
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={20}
                  height={20}
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#eba8bf"
                    d="M17 16V7H8V5h11v11zm-5 5v-9H3v-2h11v11z"
                  ></path>
                </svg>{" "}
                Doctor Appointment Details
              </button>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteWithConfirmation
        isOpen={isModalOpen}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={`Delete ${doctorToDelete?.name || "Doctor"}?`}
        message="Are you sure you want to delete this doctor's profile? This action cannot be undone."
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={isDocModalOpen}
        onClose={closeDocModal}
        documents={doctor.uploaded_documents}
        doctorName={`Dr. ${doctor.name}`}
      />

      <ToastContainer position="top-right" />

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
